import { Interpolation, Theme } from '@emotion/react'
import { FC, useEffect, useRef, useState } from 'react'

interface InViewPortProps {
    threshold?: number
    root?: Element | Document | null
}

const defaultOptions: InViewPortProps = {
    threshold: 0,
    root: null,
}

function useInViewPort(options?: InViewPortProps) {
    const { threshold, root } = { ...defaultOptions, ...options }

    const nodeRef = useRef<Element | null>(null)
    const observerRef = useRef<IntersectionObserver | null>(null)

    const [isVisible, setIsVisible] = useState<boolean>(false)

    useEffect(() => {
        if (nodeRef.current !== null) {
            observerRef.current = new IntersectionObserver(
                entries => {
                    setIsVisible(entries[0].isIntersecting)
                },
                { threshold, root }
            )

            observerRef.current.observe(nodeRef.current)
        }

        return () => {
            if (observerRef.current !== null) {
                observerRef.current.disconnect()
            }
        }
    }, [])

    useEffect(() => {
        if (isVisible && observerRef.current !== null) {
            observerRef.current.disconnect()
        }
    }, [isVisible])

    return [nodeRef, isVisible] as const
}

interface LazyComponentProps {
    fallback: JSX.Element // Component to render while content is loading
    children: JSX.Element // Content to render when in viewport
    style?: React.CSSProperties
}

export const LazyComponent: FC<LazyComponentProps> = ({
    fallback,
    children,
    style,
}) => {
    const [nodeRef, isVisible] = useInViewPort()

    return (
        <div
            ref={nodeRef as React.RefObject<HTMLDivElement>}
            className={'lazy-me'}
            style={style}
        >
            {isVisible ? children : fallback}
        </div>
    )
}

export default useInViewPort

